import { curry } from 'ramda'
import {
  DASH_TYPE_R_AU_PANEL_GAUGE_PROXIMITY_MAP,
  DASH_TYPE_R_AU_PANEL_GAUGE_LOCATIONS,
  doesALocationHaveAComponent,
} from 'rules/dash/allowedLocationsRules/rulesByModelCode/utils'
import { STAGED_PANEL_PART } from 'components/SwitchAndGauge/definitions/dashConstants'

/**
 * A "hardwired-component" type cannot move between panels.
 */
const hardwiredComponentType = {
  name: 'hardwiredComponentType',
  rule: curry((state, component, location) => {
    const { locations } = state
    const componentLocation = locations[component.location]
    if (!componentLocation) return true
    return componentLocation.panel === location?.panel
  }),
}

/**
 * A "hardwired-switch" type cannot move between panels.
 */
const hardwiredSwitchType = {
  name: 'hardwiredSwitchType',
  rule: hardwiredComponentType.rule,
}

/**
 * A "hardwired-component" of type "2-INCH GAUGE" can move between AG and AU panels.
 */
const gaugeHardwiredComponentType = {
  name: 'gaugeHardwiredComponentType',
  rule: curry((state, component, location) => {
    const { locations } = state
    const { location: componentLocation } = component
    const { panel: locationPanel } = location
    const componentPanel = locations[componentLocation].panel

    return (
      ['AGFAC', 'AUPNL'].includes(componentPanel) &&
      ['AGFAC', 'AUPNL'].includes(locationPanel)
    )
  }),
}

const AG_GAUGE_LOCATIONS = ['AG001', 'AG002', 'AG003']

/**
 * For a "hardwired-component" of type "2-INCH GAUGE" on a pre-production vehicle.
 * The AG gauge locations must be filled before a gauge is allowed on the AU panel.
 */
const preProductionGaugePanelLocationsFilled = {
  name: 'preProductionGaugePanelLocationsFilled',
  rule: curry((state, component, location) => {
    // Rule does not apply to staged gauges, cannot stage gauge from AG panel
    if (component.location.includes(STAGED_PANEL_PART)) return true

    const { components } = state
    const isToLocationOnAuPanel = location.name.includes('AU')
    const isFromLocationOnAuPanel = component.location.includes('AU')
    const isFromLocationOnAgPanel = component.location.includes('AG')
    const isToLocationOnAgPanel = location.name.includes('AG')

    const isToLocationFilled = Boolean(components[location.name])
    const areAllAgGaugeLocationsFilled = AG_GAUGE_LOCATIONS.every((location) =>
      Boolean(components[location])
    )

    // In this rule, allow all movements to an AG panel location.
    if (isToLocationOnAgPanel) return true

    // In this rule, movements to and around the AU panel are controlled.
    if (isToLocationOnAuPanel) {
      // All AG panel gauge locations must contain a component.
      if (areAllAgGaugeLocationsFilled) {
        // If going from an AG panel location.
        if (isFromLocationOnAgPanel) {
          // Only swaps are allowed. Else a hole would be created, violating the rule.
          return isToLocationFilled
        }

        // If from and to are both on AU panel, in this rule, movement is allowed.
        if (isFromLocationOnAuPanel) return true
      }

      // If not all AG gauge locations are filled, movement is not allowed.
      return false
    }

    // The default state for rules.
    return true
  }),
}

/**
 * If there are two (2) or more gauge in the AU panel.
 * Then proximity rules need to be run.
 * @param {object} state
 * @param {object} location
 * @return {boolean}
 */
const shouldUseProximityRules = ({ components }, { name }) => {
  // Proximity rules only apply to AU panel locations.
  if (!name.includes('AU')) return false

  const gaugesOnAuPanel = DASH_TYPE_R_AU_PANEL_GAUGE_LOCATIONS.filter(
    (location) => Boolean(components[location])
  )

  return gaugesOnAuPanel.length >= 2
}

/**
 * For a "hardwired-component" of type "2-INCH GAUGE" on a pre-production vehicle.
 * If there are two (2) or more gauges on the AU panel.
 * A gauge on the AU panel must be within one (1) gauge position of another.
 */
const preProductionGaugeProximity = {
  name: 'preProductionGaugeProximity',
  rule: curry((state, component, location) => {
    // Proximity rules only apply for AU gauge locations.
    // There must be two (2) or more gauges on the AU panel to apply proximity rules.
    const applyProximityRules = shouldUseProximityRules(state, location)

    if (!applyProximityRules) return true

    const map = DASH_TYPE_R_AU_PANEL_GAUGE_PROXIMITY_MAP

    // Rule: If moving to an empty location, find what that location connects with it,
    // minus the moving location. Since it will be empty after the move.
    // If a connection can be made, movement is allowed.

    // The list of locations that the to location connects with.
    const { connectsWith } = map[location.name]

    const isToLocationEmpty = !state.components[location.name]

    // If the to location is empty, filter out the from location in the connection list. It will be empty after movement.
    const connectingLocations = isToLocationEmpty
      ? connectsWith.filter((loc) => loc !== component.location)
      : connectsWith

    // Is there a component in proximity of the to location where a connection can be made?
    const isInProximity = doesALocationHaveAComponent(
      connectingLocations,
      state.components
    )

    return isInProximity
  }),
}

export const rules = {
  'hardwired-switch': [hardwiredSwitchType],
  'hardwired-component': [hardwiredComponentType],
  '2-INCH GAUGE--hardwired-component': [gaugeHardwiredComponentType],
  'preProduction--2-INCH GAUGE--hardwired-component': [
    preProductionGaugePanelLocationsFilled,
    preProductionGaugeProximity,
  ],
  'preProduction--2-INCH GAUGE--plug': [preProductionGaugePanelLocationsFilled],
}
